import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { trackBusinessEvent } from "@/lib/track-event";

export type PopupType = 'signup_credits' | 'early_exit' | 'walkthrough' | 'free_cro_analysis' | 'free_plan' | 'newsletter' | null;

interface ExitIntentState {
  isVisible: boolean;
  popupType: PopupType;
  isLoggedIn: boolean | null;
  isNewsletterSubscriber: boolean;
}

interface UseExitIntentLogicOptions {
  enabledPages?: string[];
  delay?: number;
}

const STORAGE_KEYS = {
  EARLY_EXIT_SHOWN: "exit_popup_early_shown",
  OFFER_SHOWN: "exit_popup_offer_shown",
  NEWSLETTER_SUBSCRIBED: "newsletter_subscribed",
  SESSION_START: "exit_intent_session_start",
};


export function useExitIntentLogic({
  enabledPages = ["/", "/blog", "/pricing", "/features", "/solutions", "/how-it-works", "/agencies"],
  delay = 2000,
}: UseExitIntentLogicOptions = {}) {
  const location = useLocation();
  const [state, setState] = useState<ExitIntentState>({
    isVisible: false,
    popupType: null,
    isLoggedIn: null,
    isNewsletterSubscriber: false,
  });

  const sessionStartRef = useRef<number>(Date.now());
  const isTrackingRef = useRef(false);

  // Initialize session timer on first enabled page visit
  useEffect(() => {
    const strippedPath = location.pathname.replace(/^\/(en|el)(\/|$)/, '/');
    const isEnabledPage = enabledPages.some(page =>
      strippedPath === page || strippedPath.startsWith(page + "/")
    );
    if (!isEnabledPage) return;

    const existing = sessionStorage.getItem(STORAGE_KEYS.SESSION_START);
    if (!existing) {
      const now = Date.now();
      sessionStorage.setItem(STORAGE_KEYS.SESSION_START, now.toString());
      sessionStartRef.current = now;
    } else {
      const parsed = parseInt(existing, 10);
      sessionStartRef.current = Number.isFinite(parsed) ? parsed : Date.now();
    }
  }, [location.pathname, enabledPages]);

  // Check auth status
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const isLoggedIn = !!session;
      const isNewsletterSubscriber = localStorage.getItem(STORAGE_KEYS.NEWSLETTER_SUBSCRIBED) === "true";
      setState(prev => ({ ...prev, isLoggedIn, isNewsletterSubscriber }));
    };

    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => {
      checkAuth();
    });

    return () => subscription.unsubscribe();
  }, []);

  const getTimeOnPage = useCallback((): number => {
    return Date.now() - sessionStartRef.current;
  }, []);

  const showPopup = useCallback((type: PopupType) => {
    if (!type) return;
    setState(prev => ({ ...prev, isVisible: true, popupType: type }));
    trackBusinessEvent("exit_intent_shown", { page: window.location.pathname, offer_type: type });
  }, []);

  const closePopup = useCallback(() => {
    setState(prev => ({ ...prev, isVisible: false, popupType: null }));
  }, []);

  const markNewsletterSubscribed = useCallback(() => {
    localStorage.setItem(STORAGE_KEYS.NEWSLETTER_SUBSCRIBED, "true");
    setState(prev => ({ ...prev, isNewsletterSubscriber: true }));
  }, []);

  // Main exit intent detection
  useEffect(() => {
    if (state.isLoggedIn === null) return; // Wait for auth check
    // Logged-in users never see popups
    if (state.isLoggedIn) return;

    const strippedPath = location.pathname.replace(/^\/(en|el)(\/|$)/, '/');
    const isEnabledPage = enabledPages.some(page =>
      strippedPath === page || strippedPath.startsWith(page + "/")
    );
    if (!isEnabledPage) return;

    const startTracking = () => {
      isTrackingRef.current = true;
    };

    const handleMouseLeave = (e: MouseEvent) => {
      if (!isTrackingRef.current) return;
      if (e.clientY > 0) return; // Only trigger when mouse leaves from top

      const earlyShown = sessionStorage.getItem(STORAGE_KEYS.EARLY_EXIT_SHOWN) === "true";
      const offerShown = sessionStorage.getItem(STORAGE_KEYS.OFFER_SHOWN) === "true";

      // Mutual exclusion: only ONE popup per session (early_exit OR offer)
      if (earlyShown || offerShown) return;

      // One offer for everyone who is leaving: a free account with credits.
      sessionStorage.setItem(STORAGE_KEYS.EARLY_EXIT_SHOWN, "true");
      sessionStorage.setItem(STORAGE_KEYS.OFFER_SHOWN, "true");
      showPopup('signup_credits');
    };

    const trackingTimeout = setTimeout(startTracking, delay);
    document.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      clearTimeout(trackingTimeout);
      document.removeEventListener("mouseleave", handleMouseLeave);
      isTrackingRef.current = false;
    };
  }, [
    state.isLoggedIn,
    state.isNewsletterSubscriber,
    location.pathname,
    enabledPages,
    delay,
    getTimeOnPage,
    showPopup,
  ]);

  return {
    ...state,
    closePopup,
    markNewsletterSubscribed,
  };
}
